'use client';

import type { ReactElement } from 'react';
import { useJoyride, type EventHandler, type Options, type Props, type Step } from 'react-joyride';

import { ArticTourTooltip } from './artic-tour-tooltip';

/**
 * Global step defaults tuned for driving a tour over Radix menus / cmdk lists:
 *
 * - `disableFocusTrap` — the targets live inside Radix menus that run their own
 *   focus management; letting Joyride also trap focus makes the two fight.
 * - `blockTargetInteraction` — Artic tours demonstrate rather than drive real
 *   mutations, so a stray click on the spotlit control can't fire (e.g. a
 *   partner switch that reloads the page). The tour advances via the tooltip.
 * - `overlayClickAction: false` — advance only through the tooltip buttons.
 * - `skipBeacon` — auto-open each step's tooltip; guided walkthrough, no beacon.
 *
 * All of these are overridable per-call via {@link ArticTourProps.options} (and
 * per-step, since react-joyride steps accept the same `Options` keys).
 */
const ARTIC_TOUR_OPTIONS: Partial<Options> = {
  skipBeacon: true,
  disableFocusTrap: true,
  blockTargetInteraction: true,
  overlayClickAction: false,
  spotlightPadding: 6,
  spotlightRadius: 8,
  buttons: ['skip', 'back', 'primary'],
  // Neutral dark dim that reads correctly over both light and dark surfaces.
  overlayColor: 'rgba(9, 9, 11, 0.5)',
  zIndex: 200,
};

export interface ArticTourProps {
  /** The tour's steps. Target strings, elements, refs, or `() => HTMLElement`. */
  steps: Step[];
  /** Run/stop the tour. */
  run: boolean;
  /** Sequential Next-button tour. @default true */
  continuous?: boolean;
  /** Set to enter controlled mode (drive the index yourself via `onEvent`). */
  stepIndex?: number;
  /** Called on every Joyride event — end the tour on FINISHED / SKIPPED here. */
  onEvent?: EventHandler;
  /** Per-call option overrides, merged over the Artic defaults. */
  options?: Partial<Options>;
  /** Style overrides (overlay, spotlight, beacon…). The tooltip is fully custom. */
  styles?: Props['styles'];
}

/**
 * Artic-styled react-joyride tour. Wraps `useJoyride` with the Industrial
 * tooltip and the menu-friendly defaults above, and returns the `Tour` element
 * to render. Presentational only — the consumer owns the steps, the `run`
 * state, and any UI the steps drive through their `before` / `after` hooks.
 */
export function ArticTour({
  steps,
  run,
  continuous = true,
  stepIndex,
  onEvent,
  options,
  styles,
}: ArticTourProps): ReactElement | null {
  // Build conditionally: under `exactOptionalPropertyTypes` an optional prop
  // can't receive an explicit `undefined`, so only include the ones that are set.
  const joyrideProps: Props = {
    steps,
    run,
    continuous,
    tooltipComponent: ArticTourTooltip,
    options: { ...ARTIC_TOUR_OPTIONS, ...options },
    ...(stepIndex !== undefined ? { stepIndex } : {}),
    ...(onEvent !== undefined ? { onEvent } : {}),
    ...(styles !== undefined ? { styles } : {}),
  };

  const { Tour } = useJoyride(joyrideProps);

  return Tour;
}
